<template>
  <form autocomplete="off" @submit.prevent="submitContactUsForm()" id="contact-us-form">
    <div class="text-14px py-2">
      Get in touch with a Suncorp Staff Financial Services Specialist
    </div>

    <div class="md:flex py-2 gap-4">
      <BaseInput
        name="firstName"
        type="text"
        :value="firstName"
        :error-message="errors.firstName"
        placeholder="First Name"
        class="flex-1 mb-4 md:mb-0"
        @update:value="updateFirstName" />
      <BaseInput
        name="lastName"
        type="text"
        :value="lastName"
        :error-message="errors.lastName"
        placeholder="Last Name"
        class="flex-1"
        @update:value="updateLastName" />
    </div>

    <BaseInput
      name="email"
      type="email"
      :value="email"
      :disabled="authenticated"
      :error-message="errors.email"
      placeholder="name@email.com"
      class="py-2"
      @update:value="updateEmail" />

    <div class="md:flex gap-3 py-2">
      <input-label
        for="suncorp-discussion"
        label="What would you like to discuss?"
        class="set-w-48 paragraph-2" />
      <div class="grid grid-cols-3 gap-3">
        <input-check-box
          v-for="(question, index) in discussions"
          :name="`suncorp-${index}`"
          :label="question.name"
          :key="index"
          :checked="question.value"
          @click="
            (value) => {
              changeDiscussionValue(index, value)
            }
          " />
      </div>
    </div>

    <div class="md:flex pt-4">
      <label :for="name" class="set-w-48 pt-4 paragraph-2"> Message </label>
      <input-text-area
        class="py-4 w-full"
        rows="8"
        place-holder="Message"
        name="contact-us-message"
        v-model="message"
        :error="errors.message" />
    </div>

    <div class="flex justify-center py-2">
      <OtivoButton :loading="loading" @click="submitContactUsForm()"> Send </OtivoButton>
    </div>
  </form>
</template>

<script>
import InputCheckBox from '@/components/BasicInputs/InputCheckBox.vue'
import InputLabel from '@/components/BasicInputs/InputLabel.vue'
import InputTextArea from '@/components/BasicInputs/InputTextArea.vue'
import BaseInput from '@/components/Inputs/BaseInput.vue'
import MiscellaneousService from '@/services/MiscellaneousService'
import { useUserStore } from '@/store/pinia/UserStore'
import { useSessionStore } from '@/store/pinia/SessionStore'
import { validateEmailAddress } from '@/lib/ValidationHelper'
import OtivoButton from '@/components/OtivoButton.vue'
import { useToast } from '@/composables/useToast.ts'

const { successToast, errorToast } = useToast()

export default {
  name: 'SunCorpContactUsForm',

  components: {
    OtivoButton,
    InputLabel,
    InputCheckBox,
    BaseInput,
    InputTextArea,
  },
  emits: ['close'],

  setup() {
    const userStore = useUserStore()
    const sessionStore = useSessionStore()
    const { successToast, errorToast } = useToast()
    
    return {
      userStore,
      sessionStore,
      successToast,
      errorToast
    }
  },

  data() {
    return {
      message: '',
      firstName: '',
      lastName: '',
      email: '',

      discussions: [
        { name: 'Home lending', value: false },
        { name: 'Credit cards', value: false },
        { name: 'Superannuation', value: false },
        { name: 'Personal loans', value: false },
        { name: 'Transaction accounts', value: false },
        { name: 'Term Deposits', value: false },
        { name: 'Personal Insurance', value: false },
        { name: 'Life Insurance', value: false },
        { name: 'Business Insurance', value: false },
      ],

      loading: false,

      errors: {
        firstName: '',
        lastName: '',
        email: '',
        message: '',
      },
    }
  },

  computed: {
    authenticated() {
      return this.sessionStore.isAuthenticated
    }
  },

  mounted() {
    // Initialize with user data
    const user = this.userStore.getUser
    this.firstName = user?.first_name || ''
    this.lastName = user?.last_name || ''
    this.email = user?.email || ''
  },

  methods: {
    updateFirstName(value) {
      this.firstName = value
      this.errors.firstName = ''
    },

    updateLastName(value) {
      this.lastName = value
      this.errors.lastName = ''
    },

    updateEmail(value) {
      this.email = value
      this.errors.email = ''
    },

    changeDiscussionValue(index, value) {
      this.discussions = this.discussions.map((el, i) => {
        if (i === index) el.value = value
        return el
      })
    },

    submitContactUsForm() {
      // validation
      // except discussions
      let validated = true

      if (this.firstName.length === 0) {
        validated = false
        this.errors.firstName = 'First name is required'
      }

      if (this.lastName.length === 0) {
        validated = false
        this.errors.lastName = 'Last name is required'
      }

      if (!validateEmailAddress(this.email)) {
        validated = false
        this.errors.email = 'A valid email is required'
      }

      if (this.message.length === 0) {
        validated = false
        this.errors.message = 'Please provide a message'
      }

      if (validated) {
        this.loading = true
        MiscellaneousService.submitSunCorpContactUsForm({
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          discussions: this.discussions
            .filter((el) => el.value)
            .map((el) => el.name.toLowerCase().replace(' ', '_'))
            .join(','),
          message: this.message,
        })
          .then(() => {
            this.loading = false
            successToast('We got your message, we will be in touch.')
            this.message = ''
            this.$store.dispatch('renderContactUsModal', false)
            this.$emit('close')
          })
          .catch((err) => {
            this.loading = false
            errorToast('Error occurred, please try again.')
            this.$store.dispatch('renderContactUsModal', false)
            throw err
          })
      }
    },
  },
}
</script>

<style scoped></style>
